import { StaticScreenProps, useNavigation } from "@react-navigation/native";
import { useCallback, useEffect, useState } from "react";
import { KeyboardAvoidingView } from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";

import { ChatActionBar, ChatMessages, Composer } from "../components";
import {
  isPlatformAndroid,
  useChat,
  useChatChannel,
  useChatMessageStore,
  useChatRead,
} from "../services";
import { createStyles } from "../theme";
import { useInit } from "../services/epox/utils";

type Props = StaticScreenProps<{
  id: number;
  userName: string;
}>;

export function ChatScreen({ route }: Props) {
  // Params
  const { id, userName } = route.params;

  // Data
  const { data: chat, refetch } = useChat(id);
  const [storeChatMessage, isCreating] = useChatMessageStore({ chatId: id });
  const [readChat] = useChatRead({ chatId: id });

  // State
  const [isOtherTyping, setOtherTyping] = useState(false);

  // Vars
  const navigation = useNavigation();

  // Methods
  const broadcastTyping = useChatChannel(id, {
    onTyping: () => {
      setOtherTyping(true);

      setTimeout(() => {
        setOtherTyping(false);
      }, 900);
    },
  });

  // Handlers
  const handleChange = useCallback(
    (text: string) => {
      if (text) {
        broadcastTyping();
      }
    },
    [broadcastTyping]
  );

  const handleSubmit = useCallback(
    async (text: string) => {
      storeChatMessage({ text });
    },
    [storeChatMessage]
  );

  // Effects
  useEffect(() => {
    navigation.setOptions({
      headerRight: () => (
        <ChatActionBar id={id} blocked={!!chat?.blocked} userName={userName} />
      ),
    });
  }, [chat?.blocked, id, navigation, userName]);

  // Mark chat as read upon landing
  useInit(() => {
    readChat();
  });

  // Render
  return (
    <SafeAreaView style={$.outer}>
      <KeyboardAvoidingView
        behavior={isPlatformAndroid ? undefined : "padding"}
        style={$.container}
      >
        <ChatMessages
          chat={chat}
          disabled={!!chat?.blocked}
          typing={isOtherTyping}
          onRefresh={refetch}
        />
      </KeyboardAvoidingView>
      <Composer
        disabled={!!chat?.blocked}
        loading={isCreating}
        onChange={handleChange}
        onSubmit={handleSubmit}
        placeholder={chat?.blocked ? "Client blocked." : undefined}
      />
    </SafeAreaView>
  );
}

const $ = createStyles({
  outer: {
    flex: 1,
  },
  container: {
    flex: 1,
    backgroundColor: "surface",
  },
});
